import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MfeConfig } from '@carvana/microfrontend';
import { MfeReact } from '@carvana/microfrontend-react';
import {
  DashboardModal,
  ErrorBoundary,
  ScrollToTop
} from '@carvana/purchase-helpers';
import { Loading } from '@carvana/purchase-styles';
import { registry } from 'Store';
import { EventTracking } from '@carvana/purchase-utils';
import { useTrackClickEvents } from '@carvana/vdp-react-hooks';
import Header from './components/header';
import Sidebar from './components/sidebar';
import { PageWrapper, ContainerWrapper, MainWrapper } from './app.styles';
import Routes from './routes';
import './ducks/reducer';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { AppFrameworkContextProvider } from './contexts/app-framework';
import useLoggedInGuard from './hooks/use-logged-in-guard';
import usePurchaseHeaderEvent from './hooks/use-purchase-header-event';
import { ModalContextProvider } from './contexts/modals';
import { WebSubmitProvider } from './contexts/submit';

const App = props => {
  const { trackEvent } = useContext(AnalyticsContext);
  const [state, setState] = useState({ isLoading: true });
  const { showMobileSidebar, isMobile, GetCoreConfig } = props;
  const { isLoading } = state;
  const shouldShowMobileSidebar = showMobileSidebar && isMobile;
  useTrackClickEvents(trackEvent);
  useLoggedInGuard({ readyFlag: !isLoading });
  usePurchaseHeaderEvent();

  useEffect(() => {
    (async () => {
      await MfeConfig({
        name: 'purchase-ui',
        ENV: process.env.LOCAL === 'true' ? 'local' : process.env.NODE_ENV,
        debug: process.env.NODE_ENV !== 'production',
        Render: MfeReact,
        globals: {
          React: () => import('react'),
          ReactDOM: () => import('react-dom'),
          styled: () => import('styled-components'),
          'styled-components': () => import('styled-components'),
          PropTypes: () => import('prop-types'),
          'prop-types': () => import('prop-types')
        },
        files: [
          [
            'HeaderModule',
            [
              {
                component: 'default',
                default: {
                  file: 'index.js',
                  manifest: `${process.env.ASSETS_CDN_URL}/header/module/assets-manifest.json`
                }
              }
            ]
          ],
          [
            'CoreSupportDropdown',
            [
              {
                component: 'default',
                default: {
                  file: 'index.js',
                  manifest: `${process.env.SUPPORT_AND_CONTACT_BASE_URL}/support-dropdown-module/assets-manifest.json`
                }
              }
            ]
          ]
        ]
      });

      await GetCoreConfig().catch(() => {});

      setState({ isLoading: false });
    })();
  }, []);

  return (
    <PageWrapper showMobileSidebar={shouldShowMobileSidebar}>
      {shouldShowMobileSidebar && <Sidebar />}

      <ContainerWrapper showMobileSidebar={shouldShowMobileSidebar}>
        <Header isLoading={isLoading} />
        <ErrorBoundary>
          {isLoading && <Loading />}

          {!isLoading && (
            <>
              <MainWrapper>
                <ScrollToTop>
                  <WebSubmitProvider>
                    <AppFrameworkContextProvider>
                      <ModalContextProvider>
                        <Routes {...props} registry={registry} />
                      </ModalContextProvider>
                    </AppFrameworkContextProvider>
                  </WebSubmitProvider>
                </ScrollToTop>
              </MainWrapper>
            </>
          )}
          <EventTracking registry={registry} />
          <DashboardModal registry={registry} />
        </ErrorBoundary>
      </ContainerWrapper>
    </PageWrapper>
  );
};

App.propTypes = {
  showMobileSidebar: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  GetCoreConfig: PropTypes.func.isRequired
};

export default App;
